import { countStyleGuideWords, STYLE_GUIDE_MAX_WORDS } from "../../../../src/shared/styleGuideWordLimit";
import React, { useEffect, useRef, useState } from "react";
import InlineSVG from "react-inlinesvg";
import Select from "react-select";
import styled from "styled-components/macro";
import IconButton from "src/components/IconButton";
import { createSelectStyles, createSelectTheme } from "src/styles/a11y";
import { editorTypographyStyles } from "../TiptapDocumentSB/EditorContainer/editorContentStyles";
import { StyleGuideSecondaryButton } from "./StyleGuideButtons";
import { StyleInstructionsEditor } from "./StyleInstructionsEditor";

import { SimpleStyleControlFields } from "./SimpleStyleControlFields";
import type { SimpleStyleControls } from "../../../../src/shared/simpleStyleControls";

const ASSET_ROOT = "/assets/style-guide/settings";
const STYLE_GUIDE_SELECT_MENU_Z_INDEX = 1000000001;

const generateSampleModelSelectStyles = createSelectStyles({
  control: (base: any) => ({
    ...base,
    minHeight: 40,
    border: "1px solid var(--grey-3)",
    borderRadius: 8,
    background: "var(--menu-color)",
    boxShadow: "none",
    cursor: "pointer",
    outline: "none",
    fontSize: 14,
    fontWeight: 500,
    "&:hover": {
      borderColor: "var(--grey-3)",
    },
  }),
  valueContainer: (base: any) => ({
    ...base,
    padding: "0 4px 0 12px",
    justifyContent: "flex-start",
    textAlign: "left",
  }),
  singleValue: (base: any) => ({
    ...base,
    color: "var(--grey-0)",
    textAlign: "left",
  }),
  dropdownIndicator: (base: any) => ({
    ...base,
    color: "var(--grey-0)",
    padding: "8px 10px 8px 4px",
  }),
  indicatorsContainer: (base: any) => ({
    ...base,
    paddingTop: 0,
  }),
  menu: (base: any) => ({
    ...base,
    zIndex: STYLE_GUIDE_SELECT_MENU_Z_INDEX,
    background: "var(--menu-color)",
    color: "var(--grey-0)",
    boxShadow: "var(--default-drop-shadow)",
  }),
  menuPortal: (base: any) => ({
    ...base,
    zIndex: STYLE_GUIDE_SELECT_MENU_Z_INDEX,
  }),
  menuList: (base: any) => ({
    ...base,
    maxHeight: 260,
    paddingTop: 0,
    paddingBottom: 0,
  }),
  option: (base: any, state: any) => ({
    ...base,
    background: state.isSelected
      ? "var(--selected-element-background)"
      : state.isFocused
        ? "var(--menu-color-hover)"
        : "var(--menu-color)",
    color: state.isSelected ? "var(--selected-element-text)" : "var(--grey-0)",
    cursor: "pointer",
    fontSize: 14,
    fontWeight: 500,
    textAlign: "left",
  }),
});

export type StyleEditorTab = "foundation" | "limits" | "instructions" | "writingSample";

interface StyleModelOption {
  label: string;
  value: string;
}

interface StyleEditorSurfaceProps {
  mode: "modal" | "embedded";
  testId: string;
  titleId: string;
  styleName: string;
  styleNameLabel?: string;
  readOnlyStyleName?: boolean;
  onStyleNameChange: (value: string) => void;
  onStyleNameBlur: () => void;
  instructions: string;
  instructionsWordLimit?: number;
  simpleControls?: SimpleStyleControls;
  onSimpleControlsChange?: (controls: SimpleStyleControls) => void;
  instructionsRef?: React.Ref<HTMLTextAreaElement>;
  onInstructionsChange: (value: string) => void;
  onInstructionsBlur: (value: string) => void;
  activeTab: StyleEditorTab;
  onTabSelect: (tab: StyleEditorTab) => void;
  mainRef?: React.Ref<HTMLDivElement>;
  instructionsSectionRef?: React.Ref<HTMLDivElement>;
  writingSampleSectionRef?: React.Ref<HTMLDivElement>;
  onBack: () => void;
  onClose: () => void;
  onBackdropClick?: () => void;
  onCopyInstructions: () => boolean | Promise<boolean>;
  onGenerateSample: () => void;
  generateSampleModelOptions?: StyleModelOption[];
  generateSampleModel?: string;
  onGenerateSampleModelChange?: (value: string) => void;
  isSaving?: boolean;
  isGeneratingSample?: boolean;
  generatedSample?: string;
  generateSampleError?: string;
  saveStyleError?: string;
  backLabel?: string;
  closeLabel?: string;
  generateDisabled?: boolean;
  mainTestId?: string;
  instructionsSectionTestId?: string;
  writingSampleSectionTestId?: string;
  generatedSampleTestId?: string;
}

export function StyleEditorSurface({
  mode,
  testId,
  titleId,
  styleName,
  styleNameLabel = "Style name",
  readOnlyStyleName = false,
  onStyleNameChange,
  onStyleNameBlur,
  instructions,
  instructionsWordLimit = STYLE_GUIDE_MAX_WORDS,
  simpleControls = {},
  onSimpleControlsChange,
  instructionsRef,
  onInstructionsChange,
  onInstructionsBlur,
  activeTab,
  onTabSelect,
  mainRef,
  instructionsSectionRef,
  writingSampleSectionRef,
  onBack,
  onClose,
  onBackdropClick,
  onCopyInstructions,
  onGenerateSample,
  generateSampleModelOptions = [],
  generateSampleModel,
  onGenerateSampleModelChange,
  isSaving = false,
  isGeneratingSample = false,
  generatedSample = "",
  generateSampleError = "",
  saveStyleError = "",
  backLabel = "Back to styles",
  closeLabel = "Close style editor",
  generateDisabled = false,
  mainTestId,
  instructionsSectionTestId,
  writingSampleSectionTestId,
  generatedSampleTestId,
}: StyleEditorSurfaceProps) {
  const instructionWordCount = countStyleGuideWords(instructions);
  const instructionsOverLimit = instructionWordCount > STYLE_GUIDE_MAX_WORDS;
  const instructionsExceedSaveLimit = instructionWordCount > instructionsWordLimit;
  const foundationRef = useRef<HTMLElement | null>(null);
  const limitsRef = useRef<HTMLElement | null>(null);
  const selectControlsTab = (tab: "foundation" | "limits") => {
    onTabSelect(tab);
    const target = tab === "foundation" ? foundationRef.current : limitsRef.current;
    const main = target?.parentElement;
    if (target && main) main.scrollTo({ top: target.offsetTop - main.offsetTop - 24, behavior: "auto" });
  };
  const [instructionsCopied, setInstructionsCopied] = useState(false);
  const [isEditingStyleName, setIsEditingStyleName] = useState(false);
  const instructionsCopiedTimeoutRef = useRef<number | null>(null);
  const styleNameInputRef = useRef<HTMLInputElement | null>(null);
  const selectedGenerateSampleModel =
    generateSampleModelOptions.find((option) => option.value === generateSampleModel) ?? generateSampleModelOptions[0] ?? null;

  useEffect(() => {
    return () => {
      if (instructionsCopiedTimeoutRef.current !== null) {
        window.clearTimeout(instructionsCopiedTimeoutRef.current);
      }
    };
  }, []);

  const showCopySuccess = () => {
    setInstructionsCopied(true);
    if (instructionsCopiedTimeoutRef.current !== null) {
      window.clearTimeout(instructionsCopiedTimeoutRef.current);
    }
    instructionsCopiedTimeoutRef.current = window.setTimeout(() => {
      setInstructionsCopied(false);
      instructionsCopiedTimeoutRef.current = null;
    }, 1200);
  };

  const handleCopyInstructions = () => {
    void Promise.resolve(onCopyInstructions()).then((copied) => {
      if (copied) {
        showCopySuccess();
      }
    });
  };

  const handleBackdropClick = (event: React.MouseEvent<HTMLElement>) => {
    if (mode !== "modal" || event.target !== event.currentTarget) return;
    onBackdropClick?.();
  };

  return (
    <StyleEditorOverlay
      $mode={mode}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      data-testid={testId}
      onClick={handleBackdropClick}
    >
      <StyleEditorPanel $mode={mode} aria-busy={isSaving}>
        <StyleEditorHeader>
          <StyleEditorTitleGroup $editing={isEditingStyleName}>
            {/* Navigation flushes queued saves. Disabling on blur can swallow the click. */}
            <StyleEditorBackButton
              type="button"
              aria-label={backLabel}
              onClick={onBack}
            >
              <InlineSVG src={`${ASSET_ROOT}/settings-back-24.svg`} aria-hidden="true" />
            </StyleEditorBackButton>
            <InlineSVG
              className="style-editor-style-icon"
              src={`${ASSET_ROOT}/settings-style-32.svg`}
              aria-hidden="true"
            />
            <StyleEditorNameCluster $editing={isEditingStyleName}>
              <h2 id={titleId} aria-label={styleName.trim() || "Untitled Style"}>
                {readOnlyStyleName ? (
                  <StyleEditorNameText>{styleName}</StyleEditorNameText>
                ) : (
                  <StyleEditorNameField $editing={isEditingStyleName}>
                    <StyleEditorNameSizer aria-hidden="true">{styleName || "\u00a0"}</StyleEditorNameSizer>
                    <StyleEditorNameInput
                      ref={styleNameInputRef}
                      size={1}
                      aria-label={styleNameLabel}
                      value={styleName}
                      onChange={(event) => onStyleNameChange(event.target.value)}
                      onFocus={() => setIsEditingStyleName(true)}
                      onBlur={() => {
                        setIsEditingStyleName(false);
                        onStyleNameBlur();
                      }}
                    />
                  </StyleEditorNameField>
                )}
              </h2>
              {!readOnlyStyleName && !isEditingStyleName && (
                <IconButton
                  src="/assets/pencil.svg"
                  variant="secondary"
                  size={32}
                  iconSize={24}
                  ariaLabel="Edit style name"
                  hoverTipText="Edit style name"
                  onClick={() => {
                    const input = styleNameInputRef.current;
                    if (!input) return;
                    input.focus();
                    input.select();
                  }}
                />
              )}
            </StyleEditorNameCluster>
          </StyleEditorTitleGroup>
          {!isEditingStyleName && (
            <StyleEditorCloseButton
              type="button"
              aria-label={closeLabel}
              onClick={onClose}
            >
              <InlineSVG src={`${ASSET_ROOT}/settings-close-32.svg`} aria-hidden="true" />
            </StyleEditorCloseButton>
          )}
        </StyleEditorHeader>
        <StyleEditorBody>
          <StyleEditorNav>
            {onSimpleControlsChange && (["foundation", "limits"] as const).map((tab) => (
              <StyleEditorNavButton
                key={tab}
                type="button"
                $active={activeTab === tab}
                aria-current={activeTab === tab ? "true" : undefined}
                onClick={() => selectControlsTab(tab)}
              >
                <InlineSVG src={`${ASSET_ROOT}/settings-check-circle-24.svg`} aria-hidden="true" />
                <span>{tab === "foundation" ? "Foundation" : "Limits"}</span>
              </StyleEditorNavButton>
            ))}
            <StyleEditorNavButton
              type="button"
              $active={activeTab === "instructions"}
              aria-current={activeTab === "instructions" ? "true" : undefined}
              onClick={() => onTabSelect("instructions")}
            >
              <InlineSVG src={`${ASSET_ROOT}/settings-check-circle-24.svg`} aria-hidden="true" />
              <span>Instructions</span>
            </StyleEditorNavButton>
            <StyleEditorNavButton
              type="button"
              $active={activeTab === "writingSample"}
              aria-current={activeTab === "writingSample" ? "true" : undefined}
              onClick={() => onTabSelect("writingSample")}
            >
              <InlineSVG src={`${ASSET_ROOT}/settings-write-24.svg`} aria-hidden="true" />
              <span>Writing Sample</span>
            </StyleEditorNavButton>
          </StyleEditorNav>
          <StyleEditorMain ref={mainRef} data-testid={mainTestId}>
            {onSimpleControlsChange && (["foundation", "limits"] as const).map((group) => (
              <React.Fragment key={group}>
                <StyleEditorSection ref={group === "foundation" ? foundationRef : limitsRef}>
                  <StyleEditorSectionHeading>
                    <InlineSVG src={`${ASSET_ROOT}/settings-check-circle-32.svg`} aria-hidden="true" />
                    <span>{group === "foundation" ? "Foundation" : "Limits"}</span>
                  </StyleEditorSectionHeading>
                  {activeTab === group && saveStyleError && <SampleGenerationStatus role="status">{saveStyleError}</SampleGenerationStatus>}
                  <SimpleStyleControlFields group={group} controls={simpleControls} onChange={onSimpleControlsChange} />
                </StyleEditorSection>
                <StyleEditorDivider />
              </React.Fragment>
            ))}
            <StyleEditorSection ref={instructionsSectionRef} data-testid={instructionsSectionTestId}>
              <StyleEditorHeadingRow>
                <StyleEditorSectionHeading>
                  <InlineSVG src={`${ASSET_ROOT}/settings-check-circle-32.svg`} aria-hidden="true" />
                  <span>Instructions</span>
                </StyleEditorSectionHeading>
                <StyleEditorUtilityButton
                  type="button"
                  aria-label={instructionsCopied ? "Instructions copied" : "Copy instructions"}
                  disabled={!instructions.trim()}
                  onClick={handleCopyInstructions}
                >
                  <InlineSVG
                    src={instructionsCopied ? "/assets/check-simple.svg" : `${ASSET_ROOT}/settings-copy-24.svg`}
                    aria-hidden="true"
                  />
                </StyleEditorUtilityButton>
              </StyleEditorHeadingRow>
              <StyleEditorInstructions>
                <StyleInstructionsEditor
                  value={instructions}
                  sourceRef={instructionsRef}
                  wordLimit={instructionsWordLimit}
                  onChange={onInstructionsChange}
                  onBlur={onInstructionsBlur}
                />
                <InstructionsWordCount
                  $overLimit={instructionsOverLimit}
                  data-testid="style-instructions-word-count"
                  title={instructionsWordLimit > STYLE_GUIDE_MAX_WORDS
                    ? "The limit is 3,500 words. This existing guide can still be used, edited, or shortened, but cannot grow beyond its saved length."
                    : "Style guides can contain up to 3,500 words."}
                >
                  {instructionWordCount.toLocaleString("en-US")} / {STYLE_GUIDE_MAX_WORDS.toLocaleString("en-US")} words
                </InstructionsWordCount>
              </StyleEditorInstructions>
              {instructionsExceedSaveLimit && (
                <SampleGenerationStatus role="status">
                  Shorten the instructions to {instructionsWordLimit.toLocaleString("en-US")} words or fewer to save.
                </SampleGenerationStatus>
              )}
              {activeTab === "instructions" && saveStyleError && !instructionsExceedSaveLimit && (
                <SampleGenerationStatus role="status">{saveStyleError}</SampleGenerationStatus>
              )}
            </StyleEditorSection>
            <StyleEditorDivider />
            <StyleEditorSection ref={writingSampleSectionRef} data-testid={writingSampleSectionTestId}>
              <StyleEditorSectionHeading>
                <InlineSVG src={`${ASSET_ROOT}/settings-write-32.svg`} aria-hidden="true" />
                <span>Writing Sample</span>
              </StyleEditorSectionHeading>
              <GenerateSampleControls>
                <GenerateSampleButton
                  type="button"
                  disabled={isGeneratingSample || generateDisabled || instructionsExceedSaveLimit}
                  onClick={onGenerateSample}
                >
                  <InlineSVG src={`${ASSET_ROOT}/settings-write-24.svg`} aria-hidden="true" />
                  <span>{isGeneratingSample ? "Generating..." : "Generate sample"}</span>
                </GenerateSampleButton>
                {generateSampleModelOptions.length > 0 && (
                  <GenerateSampleModelField>
                    <Select<StyleModelOption>
                      inputId="style-guide-generate-sample-model"
                      aria-label="Generate sample model"
                      classNamePrefix="style-guide-generate-sample-model"
                      value={selectedGenerateSampleModel}
                      options={generateSampleModelOptions}
                      isSearchable={false}
                      isDisabled={isGeneratingSample}
                      menuPlacement="auto"
                      theme={createSelectTheme()}
                      styles={generateSampleModelSelectStyles}
                      onChange={(option) => {
                        if (!option) return;
                        onGenerateSampleModelChange?.(option.value);
                      }}
                    />
                  </GenerateSampleModelField>
                )}
              </GenerateSampleControls>
              {generateSampleError && <SampleGenerationStatus role="status">{generateSampleError}</SampleGenerationStatus>}
              {generatedSample && (
                <GeneratedSamplePreview
                  aria-label="Generated sample preview"
                  data-generated-sample-preview
                  data-testid={generatedSampleTestId}
                >
                  {generatedSample}
                </GeneratedSamplePreview>
              )}
              {!generatedSample && (
                <EmptyWritingSampleSpacer
                  aria-hidden="true"
                  data-testid="style-editor-empty-writing-sample-spacer"
                />
              )}
              {activeTab === "writingSample" && saveStyleError && (
                <SampleGenerationStatus role="status">{saveStyleError}</SampleGenerationStatus>
              )}
            </StyleEditorSection>
          </StyleEditorMain>
          </StyleEditorBody>
        </StyleEditorPanel>
      </StyleEditorOverlay>
  );
}

const StyleEditorOverlay = styled.section<{ $mode: "modal" | "embedded" }>`
  ${({ $mode }) =>
    $mode === "modal"
      ? `
        position: fixed;
        inset: 0;
        z-index: 100000001;
        display: flex;
        align-items: center;
        justify-content: center;
        background: transparent;
      `
      : `
        position: absolute;
        inset: 0;
        z-index: 2;
        width: 100%;
        height: 100%;
        overflow: hidden;
      `}
  color: var(--grey-0);
  text-align: left;
  font-family: Inter, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  font-optical-sizing: none;
`;

const StyleEditorPanel = styled.div<{ $mode: "modal" | "embedded" }>`
  position: relative;
  width: ${({ $mode }) => ($mode === "modal" ? "min(1000px, calc(100vw - 48px))" : "100%")};
  min-width: ${({ $mode }) => ($mode === "modal" ? "min(720px, calc(100vw - 48px))" : "0")};
  height: ${({ $mode }) => ($mode === "modal" ? "min(900px, calc(100vh - 48px))" : "100%")};
  margin: ${({ $mode }) => ($mode === "modal" ? "24px 0" : "0")};
  padding: 0;
  box-sizing: border-box;
  overflow: hidden;
  border-radius: 20px;
  background: var(--menu-color);
  color: var(--grey-0);
`;

const StyleEditorHeader = styled.header`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 72px;
  padding: 0 24px;
  box-sizing: border-box;

  &::after {
    content: "";
    position: absolute;
    left: 24px;
    right: 24px;
    bottom: 0;
    height: 1px;
    background: var(--grey-3);
  }
`;

const StyleEditorTitleGroup = styled.div<{ $editing?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 16px;
  flex: 1 1 auto;
  min-width: 0;

  .style-editor-style-icon {
    width: 32px;
    height: 32px;
    flex: 0 0 32px;
  }

  .style-editor-style-icon path {
    fill: currentColor !important;
  }

  h2 {
    flex: ${({ $editing }) => ($editing ? "1 1 auto" : "0 1 auto")};
    width: ${({ $editing }) => ($editing ? "auto" : "max-content")};
    margin: 0;
    color: var(--grey-0);
    font-size: 24px;
    font-weight: 500;
    line-height: 29px;
    min-width: 0;
    max-width: 100%;
  }
`;

const StyleEditorNameCluster = styled.div<{ $editing?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex: ${({ $editing }) => ($editing ? "1 1 auto" : "0 1 auto")};
  width: ${({ $editing }) => ($editing ? "auto" : "max-content")};
  min-width: 0;
  max-width: 100%;
`;

const StyleEditorNameField = styled.span<{ $editing?: boolean }>`
  display: inline-grid;
  width: ${({ $editing }) => ($editing ? "100%" : "max-content")};
  max-width: 100%;
  min-width: 0;
`;

const StyleEditorNameSizer = styled.span`
  grid-area: 1 / 1;
  padding: 5px;
  box-sizing: border-box;
  font: inherit;
  line-height: inherit;
  white-space: pre;
  visibility: hidden;
  overflow: hidden;
`;

const StyleEditorNameInput = styled.input`
  grid-area: 1 / 1;
  width: 100%;
  min-width: 0;
  padding: 5px;
  box-sizing: border-box;
  border: 0;
  border-radius: 5px;
  background: transparent;
  color: var(--grey-0);
  font: inherit;
  line-height: inherit;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  outline: none;

  &:hover,
  &:focus {
    box-shadow: 0 0 0 2px var(--primary-cta-translucent);
  }
`;

const StyleEditorNameText = styled.span`
  display: block;
  width: 100%;
  min-width: 0;
  color: var(--grey-0);
  font: inherit;
  line-height: inherit;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const StyleEditorIconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--grey-0);
  cursor: pointer;

  &:disabled {
    cursor: default;
    opacity: 0.5;
  }

  svg path {
    fill: currentColor !important;
  }
`;

const StyleEditorBackButton = styled(StyleEditorIconButton)`
  width: 32px;
  height: 32px;

  svg {
    width: 32px;
    height: 32px;
  }
`;

const StyleEditorCloseButton = styled(StyleEditorIconButton)`
  width: 32px;
  height: 32px;

  svg {
    width: 32px;
    height: 32px;
  }
`;

const StyleEditorBody = styled.div`
  display: grid;
  grid-template-columns: 246px minmax(0, 1fr);
  @media (max-width: 640px) {
    display: flex;
    flex-direction: column;
    nav { flex-direction: row; flex-wrap: wrap; padding: 12px; gap: 4px; }
    nav button { width: auto; font-size: 13px; padding: 0 8px; }
  }
  height: calc(100% - 72px);
  min-height: 0;
`;

const StyleEditorNav = styled.nav`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px;
  box-sizing: border-box;
`;

const StyleEditorNavButton = styled.button<{ $active?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  height: 40px;
  padding: 0 12px;
  border: 0;
  border-radius: 8px;
  background: ${({ $active }) => ($active ? "var(--selected-element-background)" : "transparent")};
  color: ${({ $active }) => ($active ? "var(--selected-element-text)" : "var(--grey-0)")};
  font-size: 16px;
  font-weight: 500;
  line-height: 19.3636px;
  text-align: left;
  cursor: pointer;

  svg {
    width: 24px;
    height: 24px;
    flex: 0 0 24px;
  }

  svg path {
    fill: currentColor !important;
  }
`;

const StyleEditorMain = styled.div`
  padding: 24px;
  box-sizing: border-box;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
`;

const StyleEditorSection = styled.section`
  width: min(706px, 100%);

  textarea,
  hr {
    width: 100%;
  }

  button + textarea,
  div + textarea {
    margin-top: 24px;
  }
`;

const StyleEditorSectionHeading = styled.h3`
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0 0 24px;
  color: var(--grey-0);
  font-size: 20px;
  font-weight: 500;
  line-height: 24px;

  svg {
    width: 32px;
    height: 32px;
    flex: 0 0 32px;
  }

  svg path {
    fill: currentColor !important;
  }
`;

const StyleEditorHeadingRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: min(706px, 100%);
  margin: 0 0 24px;

  ${StyleEditorSectionHeading} {
    margin: 0;
  }
`;

const StyleEditorUtilityButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: var(--grey-0);
  cursor: pointer;

  &:hover:not(:disabled),
  &:focus-visible:not(:disabled) {
    background: var(--menu-color-hover);
    outline: none;
  }

  &:disabled {
    color: var(--grey-2);
    opacity: 0.55;
    cursor: not-allowed;
  }

  svg {
    width: 24px;
    height: 24px;
  }

  svg path {
    fill: currentColor;
  }
`;

const StyleEditorInstructions = styled.div`
  width: min(706px, 100%);
  min-height: 220px;
  margin-top: 24px;
  padding: 8px 10px;
  box-sizing: border-box;
  border: 1px solid var(--grey-3);
  border-radius: 5px;
  background: var(--menu-color);
  box-shadow: var(--tiny-drop-shadow);
  color: var(--grey-0);
  font-family: Inter, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  overflow-y: hidden;
  resize: none;
  cursor: text;
  --sw-paragraph-spacing: 12px;
  ${editorTypographyStyles("&")}

  /* Drawn discs hang 0.84em into the shared 1em gutter; native "1." is
     wider, so the same padding: 0 leaves numbers sitting further left. */
  ol {
    padding-left: 0.5em;
  }

  &:focus-within {
    border-color: var(--primary-cta);
  }

  .tiptap,
  .ProseMirror {
    outline: none;
    min-height: 204px;
  }

  .ProseMirror p.is-editor-empty:first-child::before {
    content: attr(data-placeholder);
    color: var(--grey-1);
    opacity: 0.5;
    float: left;
    height: 0;
    pointer-events: none;
  }

  .ProseMirror > *:first-child {
    margin-top: 0;
  }

  .ProseMirror a {
    color: var(--primary-cta-high-contrast);
  }
`;

const InstructionsWordCount = styled.div<{ $overLimit: boolean }>`
  margin-top: 12px;
  text-align: right;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  color: ${({ $overLimit }) => ($overLimit ? "var(--danger-color)" : "var(--grey-1)")};
`;

const StyleEditorDivider = styled.hr`
  width: min(706px, 100%);
  height: 1px;
  margin: 40px 0 39px;
  border: 0;
  background: var(--grey-3);
`;

const GenerateSampleButton = styled(StyleGuideSecondaryButton)`
  height: 40px;
  min-width: 163px;

  svg {
    width: 24px;
    height: 24px;
  }
`;

const GenerateSampleControls = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 12px;
  flex-wrap: wrap;
`;

const GenerateSampleModelField = styled.div`
  display: flex;
  flex-direction: column;
  width: 190px;
`;

const GeneratedSamplePreview = styled.div`
  width: min(706px, 100%);
  margin-top: 24px;
  color: var(--grey-0);
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  white-space: pre-wrap;
`;

const EmptyWritingSampleSpacer = styled.div`
  height: calc(min(900px, calc(100vh - 48px)) / 2);
  pointer-events: none;
`;

export const SampleGenerationStatus = styled.p`
  margin: 16px 0 0;
  color: var(--danger-color);
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;
`;
